import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { BACKEND_NODE_URL } from '../config/constants';
import CampaignModal from './CampaignModal';
import './CampaignsPage.css';
import AppIcon from './icons/AppIcon';

const CampaignsPage = () => {
  const { user } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [marketingLists, setMarketingLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [highlightedId, setHighlightedId] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [showListModal, setShowListModal] = useState(false);
  const [selectedList, setSelectedList] = useState(null);
  const [listContactsPage, setListContactsPage] = useState(0);
  const CONTACTS_PER_PAGE = 10;

  // Verificar se veio do calendário com ID específico
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const campaignId = params.get('campaign_id');
    if (campaignId) {
      setHighlightedId(campaignId);
      setTimeout(() => {
        const el = document.getElementById(`campaign-${campaignId}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 500);
    }
  }, []);

  const getAuthHeaders = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session?.access_token}`
    };
  }, []);

  const loadCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      const headers = await getAuthHeaders();
      const response = await fetch(`${BACKEND_NODE_URL}/api/campaigns`, { headers });
      if (!response.ok) throw new Error('Erro ao carregar campanhas');
      const data = await response.json();
      setCampaigns(data || []);
    } catch (error) {
      console.error('[Campaigns] Erro ao carregar:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  const loadMarketingLists = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('marketing_contact_lists')
        .select('id, list_name, total_contacts, project_id, connection_id, contacts_data, created_at')
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('[Campaigns] Erro ao carregar listas de marketing:', error);
        setMarketingLists([]);
      } else {
        // Buscar nomes de projetos e conexões
        const enrichedData = await Promise.all((data || []).map(async (list) => {
          let projectName = null;
          let connectionName = null;

          if (list.project_id) {
            const { data: project } = await supabase
              .from('projects')
              .select('name')
              .eq('id', list.project_id)
              .single();
            projectName = project?.name || null;
          }

          if (list.connection_id) {
            const { data: connection } = await supabase
              .from('connections')
              .select('name')
              .eq('id', list.connection_id)
              .single();
            connectionName = connection?.name || null;
          }

          return {
            ...list,
            project_name: projectName,
            connection_name: connectionName
          };
        }));

        console.log('[Campaigns] Listas de marketing carregadas:', enrichedData?.length);
        setMarketingLists(enrichedData || []);
      }
    } catch (error) {
      console.error('[Campaigns] Erro ao carregar listas:', error);
    }
  }, []);

  useEffect(() => {
    if (user?.company_id) {
      loadCampaigns();
      loadMarketingLists();
    }
  }, [user?.company_id, loadCampaigns, loadMarketingLists]);

  // Realtime para atualizar campanhas em tempo real
  useEffect(() => {
    if (!user?.company_id) return;

    console.log('[Campaigns] 🔄 Configurando Realtime para campanhas');

    // Subscribe para mudanças em remarketing_campaigns
    const campaignsChannel = supabase
      .channel(`remarketing_campaigns:${user.company_id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'remarketing_campaigns',
          filter: `company_id=eq.${user.company_id}`
        },
        (payload) => {
          console.log('[Campaigns] 📡 Realtime: mudança detectada', payload.eventType);
          
          if (payload.eventType === 'DELETE') {
            setCampaigns(prev => prev.filter(c => c.id !== payload.old.id));
          } else if (payload.eventType === 'UPDATE') {
            setCampaigns(prev =>
              prev.map(c => c.id === payload.new.id ? payload.new : c)
            );
          } else if (payload.eventType === 'INSERT') {
            setCampaigns(prev => [payload.new, ...prev]);
          }
        }
      )
      .subscribe();

    // Cleanup ao desmontar
    return () => {
      console.log('[Campaigns] 🔌 Desconectando Realtime de campanhas');
      supabase.removeChannel(campaignsChannel);
    };
  }, [user?.company_id]);

  const handleActivate = async (campaign) => {
    if (!confirm(`Ativar campanha "${campaign.name}"?`)) return;
    try {
      setActionLoading(campaign.id);
      const headers = await getAuthHeaders();
      const response = await fetch(`${BACKEND_NODE_URL}/api/campaigns/${campaign.id}/activate`, {
        method: 'POST', headers
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Erro ao ativar');
      alert(`Campanha ativada com ${data.total_contacts} contatos!`);
      loadCampaigns();
    } catch (error) {
      alert('Erro ao ativar: ' + error.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handlePause = async (campaign) => {
    if (!confirm(`Pausar campanha "${campaign.name}"? Os gatilhos serão removidos.`)) return;
    try {
      setActionLoading(campaign.id);
      const headers = await getAuthHeaders();
      const response = await fetch(`${BACKEND_NODE_URL}/api/campaigns/${campaign.id}/pause`, {
        method: 'POST', headers
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Erro ao pausar');
      }
      loadCampaigns();
    } catch (error) {
      alert('Erro ao pausar: ' + error.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleResume = async (campaign) => {
    if (!confirm(`Retomar campanha "${campaign.name}"?`)) return;
    try {
      setActionLoading(campaign.id);
      const headers = await getAuthHeaders();
      const response = await fetch(`${BACKEND_NODE_URL}/api/campaigns/${campaign.id}/resume`, {
        method: 'POST', headers
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Erro ao retomar');
      }
      loadCampaigns();
    } catch (error) {
      alert('Erro ao retomar: ' + error.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (campaign) => {
    if (!confirm(`Deletar campanha "${campaign.name}"? Esta ação é irreversível.`)) return;
    try {
      setActionLoading(campaign.id);
      const headers = await getAuthHeaders();
      const response = await fetch(`${BACKEND_NODE_URL}/api/campaigns/${campaign.id}`, {
        method: 'DELETE', headers
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Erro ao deletar');
      }
      loadCampaigns();
    } catch (error) {
      alert('Erro ao deletar: ' + error.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleEdit = (campaign) => {
    setEditingCampaign(campaign);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingCampaign(null);
  };

  const handleModalSave = () => {
    setShowModal(false);
    setEditingCampaign(null);
    loadCampaigns();
  };

  // Agrupar campanhas por status
  const activeCampaigns = campaigns.filter(c => c.status === 'active');
  const pausedCampaigns = campaigns.filter(c => c.status === 'paused');
  const draftCampaigns = campaigns.filter(c => c.status === 'draft');
  const completedCampaigns = campaigns.filter(c => c.status === 'completed');

  const renderProgress = (campaign) => {
    const progress = campaign.progress || {};
    const total = progress.total_contacts || campaign.total_contacts || 0;
    const sent = progress.sent_count || campaign.sent_count || 0;
    const failed = progress.failed_count || campaign.failed_count || 0;
    const pct = total > 0 ? Math.round((sent / total) * 100) : 0;

    return (
      <div className="campaign-progress">
        <div className="campaign-progress-header">
          <span className="campaign-progress-text">
            {sent}/{total} enviados {failed > 0 && `(${failed} falhas)`}
          </span>
          <span className="campaign-progress-percentage">{pct}%</span>
        </div>
        <div className="campaign-progress-bar">
          <div
            className={`campaign-progress-fill ${pct >= 100 ? 'completed' : ''}`}
            style={{ width: `${Math.min(pct, 100)}%` }}
          />
        </div>
      </div>
    );
  };

  const renderCard = (campaign) => {
    const scheduling = campaign.scheduling || {};
    const msgConfig = campaign.message_config || {};
    const isLoading = actionLoading === campaign.id;

    return (
      <div
        key={campaign.id}
        id={`campaign-${campaign.id}`}
        className={`campaign-card ${highlightedId === campaign.id ? 'highlighted' : ''}`}
      >
        <div className="campaign-card-header">
          <div>
            <h4 className="campaign-card-title">{campaign.name}</h4>
            {campaign.description && (
              <p className="campaign-card-description">{campaign.description}</p>
            )}
          </div>
          <span className={`campaign-status-badge ${campaign.status}`}>
            {campaign.status === 'draft' && 'Rascunho'}
            {campaign.status === 'active' && 'Ativa'}
            {campaign.status === 'paused' && 'Pausada'}
            {campaign.status === 'completed' && 'Concluída'}
          </span>
        </div>

        <div className="campaign-card-info">
          {campaign.project_name && (
            <div className="campaign-card-info-row">
              <AppIcon name="dashboard" />
              <span>{campaign.project_name}</span>
            </div>
          )}
          {campaign.source_list_name && (
            <div className="campaign-card-info-row">
              <AppIcon name="list" />
              <span>{campaign.source_list_name}</span>
            </div>
          )}
          <div className="campaign-card-info-row">
            <AppIcon name={msgConfig.type === 'agent' ? 'smart_toy' : 'message'} />
            <span className={`campaign-msg-type ${msgConfig.type || 'manual'}`}>
              {msgConfig.type === 'agent' ? 'Agente IA' : 
               msgConfig.content_type === 'image' ? 'Imagem' : 
               msgConfig.content_type === 'audio' ? 'Áudio' : 'Texto'}
            </span>
          </div>
        </div>

        {scheduling.daily_start_time && (
          <div className="campaign-schedule-info">
            <AppIcon name="schedule" />
            <span>{scheduling.daily_start_time} - {scheduling.daily_end_time || '18:00'}</span>
          </div>
        )}

        {(campaign.status === 'active' || campaign.status === 'completed') && renderProgress(campaign)}

        <div className="campaign-card-actions">
          {campaign.status === 'draft' && (
            <>
              <button className="campaign-action-btn activate" onClick={() => handleActivate(campaign)} disabled={isLoading}>
                <AppIcon name="play_arrow" /> Ativar
              </button>
              <button className="campaign-action-btn" onClick={() => handleEdit(campaign)} disabled={isLoading}>
                <AppIcon name="edit" /> Editar
              </button>
            </>
          )}
          {campaign.status === 'active' && (
            <button className="campaign-action-btn pause" onClick={() => handlePause(campaign)} disabled={isLoading}>
              <AppIcon name="pause" /> Pausar
            </button>
          )}
          {campaign.status === 'paused' && (
            <>
              <button className="campaign-action-btn resume" onClick={() => handleResume(campaign)} disabled={isLoading}>
                <AppIcon name="play_arrow" /> Retomar
              </button>
              <button className="campaign-action-btn" onClick={() => handleEdit(campaign)} disabled={isLoading}>
                <AppIcon name="edit" /> Editar
              </button>
            </>
          )}
          {campaign.status === 'completed' && (
            <button className="campaign-action-btn" onClick={() => handleEdit(campaign)} disabled={isLoading}>
              <AppIcon name="visibility" /> Detalhes
            </button>
          )}
          {campaign.status !== 'active' && (
            <button className="campaign-action-btn delete" onClick={() => handleDelete(campaign)} disabled={isLoading}>
              <AppIcon name="delete" />
            </button>
          )}
        </div>
      </div>
    );
  };

  const handleDeleteList = async (list) => {
    if (!confirm(`Tem certeza que deseja excluir a lista "${list.list_name}"? Esta ação não pode ser desfeita.`)) return;
    try {
      const { error } = await supabase
        .from('marketing_contact_lists')
        .delete()
        .eq('id', list.id);
      
      if (error) throw error;
      loadMarketingLists();
    } catch (error) {
      alert('Erro ao excluir lista: ' + error.message);
    }
  };

  const renderMarketingListCard = (list) => {
    const createdDate = list.created_at ? new Date(list.created_at).toLocaleDateString('pt-BR') : '-';
    
    return (
      <div key={list.id} className="campaign-card marketing-list-card">
        <div className="campaign-card-header">
          <div>
            <h4 className="campaign-card-title">{list.list_name}</h4>
            <p className="campaign-card-description">Lista de Campanha</p>
          </div>
        </div>

        <div className="campaign-card-info">
          <div className="campaign-card-info-row">
            <AppIcon name="people" />
            <span>{list.total_contacts || 0} contatos</span>
          </div>
          <div className="campaign-card-info-row">
            <AppIcon name="calendar_today" />
            <span>{createdDate}</span>
          </div>
          {list.project_name && (
            <div className="campaign-card-info-row">
              <AppIcon name="dashboard" />
              <span>{list.project_name}</span>
            </div>
          )}
          {list.connection_name && (
            <div className="campaign-card-info-row">
              <AppIcon name="phone" />
              <span>{list.connection_name}</span>
            </div>
          )}
        </div>

        <div className="campaign-card-actions">
          <button 
            className="campaign-action-btn" 
            onClick={() => {
              setSelectedList(list);
              setListContactsPage(0);
              setShowListModal(true);
            }}
          >
            <AppIcon name="visibility" /> Visualizar
          </button>
          <button 
            className="campaign-action-btn activate" 
            onClick={() => {
              setEditingCampaign(null);
              setShowModal(true);
              sessionStorage.setItem('selectedMarketingList', JSON.stringify(list));
            }}
          >
            <AppIcon name="add" /> Criar Campanha
          </button>
          <button 
            className="campaign-action-btn delete" 
            onClick={() => handleDeleteList(list)}
          >
            <AppIcon name="delete" /> Excluir
          </button>
        </div>
      </div>
    );
  };

  const renderSection = (title, icon, statusClass, items) => {
    if (items.length === 0) return null;
    return (
      <div className="campaigns-section">
        <div className="campaigns-section-header">
          <AppIcon name={icon} className={`section-icon ${statusClass}`} />
          <h3>{title}</h3>
          <span className="section-count">{items.length}</span>
        </div>
        <div className="campaigns-grid">
          {items.map(renderCard)}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="campaigns-page">
        <div className="campaigns-loading">
          <AppIcon name="sync" />
          Carregando campanhas...
        </div>
      </div>
    );
  }

  return (
    <div className="campaigns-page">
      <div className="campaigns-header">
        <h2>
          <AppIcon name="campaign" />
          Campanhas de Remarketing
        </h2>
      </div>

      {/* Seção de Listas de Marketing Disponíveis */}
      {marketingLists.length > 0 && (
        <div className="campaigns-section">
          <div className="campaigns-section-header">
            <AppIcon name="list" className="section-icon draft" />
            <h3>Listas de Campanha Disponíveis</h3>
            <span className="section-count">{marketingLists.length}</span>
          </div>
          <div className="campaigns-grid">
            {marketingLists.map(renderMarketingListCard)}
          </div>
        </div>
      )}

      {campaigns.length === 0 ? (
        <div className="campaigns-empty">
          <AppIcon name="campaign" />
          <h3>Nenhuma campanha encontrada</h3>
          <p>Crie sua primeira campanha de remarketing clicando no botão acima ou selecionando uma lista.</p>
        </div>
      ) : (
        <>
          {renderSection('Campanhas Ativas', 'play_circle', 'active', activeCampaigns)}
          {renderSection('Rascunhos', 'edit_note', 'draft', draftCampaigns)}
          {renderSection('Campanhas Pausadas', 'pause_circle', 'paused', pausedCampaigns)}
          {renderSection('Concluídas', 'check_circle', 'completed', completedCampaigns)}
        </>
      )}

      {/* Modal de Visualização de Contatos da Lista */}
      {showListModal && selectedList && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.85)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={() => setShowListModal(false)}>
          <div style={{
            background: 'var(--card-background)',
            borderRadius: '12px',
            border: '1px solid rgba(68, 225, 255, 0.3)',
            padding: '2rem',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
          }} onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, color: '#44e1ff', fontSize: '1.3rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <AppIcon name="list" />
                {selectedList.list_name}
              </h3>
              <button onClick={() => setShowListModal(false)} style={{
                background: 'none',
                border: 'none',
                color: '#44e1ff',
                cursor: 'pointer',
                fontSize: '1.5rem'
              }}>
                <AppIcon name="close" />
              </button>
            </div>

            {/* Info */}
            <div style={{ marginBottom: '1.5rem', padding: '1rem', background: 'rgba(68, 225, 255, 0.05)', borderRadius: '8px', borderLeft: '3px solid #44e1ff' }}>
              <p style={{ margin: 0, color: 'var(--text-color)', fontSize: '0.9rem' }}>
                <strong>Total de contatos:</strong> {selectedList.total_contacts || 0}
              </p>
            </div>

            {/* Tabela de Contatos */}
            {selectedList.contacts_data && selectedList.contacts_data.length > 0 ? (
              <>
                <div style={{ marginBottom: '1rem' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid rgba(68, 225, 255, 0.3)' }}>
                        <th style={{ textAlign: 'left', padding: '0.75rem', color: '#44e1ff', fontWeight: 600 }}>Nome</th>
                        <th style={{ textAlign: 'left', padding: '0.75rem', color: '#44e1ff', fontWeight: 600 }}>Telefone</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedList.contacts_data
                        .slice(listContactsPage * CONTACTS_PER_PAGE, (listContactsPage + 1) * CONTACTS_PER_PAGE)
                        .map((contact, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid rgba(68, 225, 255, 0.1)' }}>
                            <td style={{ padding: '0.75rem', color: 'var(--text-color)' }}>
                              {contact.contact_name || contact.name || '-'}
                            </td>
                            <td style={{ padding: '0.75rem', color: 'var(--text-color)' }}>
                              {contact.numero || contact.phone || '-'}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>

                {/* Paginação */}
                {Math.ceil((selectedList.contacts_data.length || 0) / CONTACTS_PER_PAGE) > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid rgba(68, 225, 255, 0.2)' }}>
                    <span style={{ color: 'var(--text-color-secondary)', fontSize: '0.9rem' }}>
                      Página {listContactsPage + 1} de {Math.ceil((selectedList.contacts_data.length || 0) / CONTACTS_PER_PAGE)}
                    </span>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        onClick={() => setListContactsPage(Math.max(0, listContactsPage - 1))}
                        disabled={listContactsPage === 0}
                        style={{
                          padding: '0.5rem 1rem',
                          background: listContactsPage === 0 ? 'rgba(68, 225, 255, 0.1)' : 'rgba(68, 225, 255, 0.2)',
                          border: '1px solid rgba(68, 225, 255, 0.3)',
                          color: '#44e1ff',
                          borderRadius: '6px',
                          cursor: listContactsPage === 0 ? 'not-allowed' : 'pointer',
                          opacity: listContactsPage === 0 ? 0.5 : 1,
                          fontSize: '0.85rem'
                        }}
                      >
                        ← Anterior
                      </button>
                      <button
                        onClick={() => setListContactsPage(Math.min(Math.ceil((selectedList.contacts_data.length || 0) / CONTACTS_PER_PAGE) - 1, listContactsPage + 1))}
                        disabled={listContactsPage >= Math.ceil((selectedList.contacts_data.length || 0) / CONTACTS_PER_PAGE) - 1}
                        style={{
                          padding: '0.5rem 1rem',
                          background: listContactsPage >= Math.ceil((selectedList.contacts_data.length || 0) / CONTACTS_PER_PAGE) - 1 ? 'rgba(68, 225, 255, 0.1)' : 'rgba(68, 225, 255, 0.2)',
                          border: '1px solid rgba(68, 225, 255, 0.3)',
                          color: '#44e1ff',
                          borderRadius: '6px',
                          cursor: listContactsPage >= Math.ceil((selectedList.contacts_data.length || 0) / CONTACTS_PER_PAGE) - 1 ? 'not-allowed' : 'pointer',
                          opacity: listContactsPage >= Math.ceil((selectedList.contacts_data.length || 0) / CONTACTS_PER_PAGE) - 1 ? 0.5 : 1,
                          fontSize: '0.85rem'
                        }}
                      >
                        Próxima →
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-color-secondary)' }}>
                <p>Nenhum contato encontrado nesta lista.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {showModal && (
        <CampaignModal
          campaign={editingCampaign}
          onClose={handleModalClose}
          onSave={handleModalSave}
        />
      )}
    </div>
  );
};

export default CampaignsPage;
